import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { useViewport } from "src/contexts";
import { Icon } from "~components";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

export type ToastMessageProps = {
  title: ReactNode;
  variant: "success" | "warning" | "error";
  body?: ReactNode;
  duration?: number;
} | null;

type ToastProps = {
  message: ToastMessageProps;
  onClose?: () => void;
};

const toastVariants = {
  warning: Css.bcYellow500.bgYellow50.$,
  success: Css.bcGreen500.bgGreen50.$,
  error: Css.bcRed500.bgRed50.$,
};

/* Slides toast into view on bottom left of screen. Slides in top down as banner on mobile. */
export const Toast = ({ message, onClose }: ToastProps) => {
  const [show, setShow] = useState(false);
  const { isMobile } = useViewport();
  const { title, body, variant, duration } = message || {};
  const tids = useTestIds({});

  useEffect(() => {
    if (message) {
      setShow(true);
      if (duration) {
        const timeout = setTimeout(() => {
          setShow(false);
        }, duration);
        return () => clearTimeout(timeout);
      }
    }
  }, [message, duration]);

  return (
    <AnimatePresence onExitComplete={() => onClose?.()}>
      {show && (
        <motion.div
          key="toast"
          // Animating up from the bottom was causing layout shifts. v8 has a fix for this
          initial={isMobile ? { opacity: 0, y: "-100%" } : { opacity: 0, x: "-500px" }}
          animate={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, x: "3.5vw" }}
          exit={isMobile ? { y: "-100%" } : { x: "-500px" }}
          css={{
            // Note: Header is z2 on mobile
            ...Css.absolute.z3.px2.pt1.pb3.bss.br4
              .bw("1px 1px 1px 4px")
              .boxShadow("-4px -4px 10px rgba(0, 0, 0, 0.1), 4px 4px 10px rgba(0, 0, 0, 0.1)")
              .ifMobile.br0.py3.w100.boxShadow("0px 4px 10px rgba(0, 0, 0, 0.1)")
              .bw("4px")
              .top0.fixed.else.bottomPx(120)
              .wPx(458).$,
            ...toastVariants[variant!],
          }}
          // Make the entire toast clickable to close on mobile
          onClick={() => isMobile && setShow(false)}
        >
          <div css={Css.df.fdc.w100.h100.$} {...tids.toast}>
            <div css={Css.df.asfe.cursorPointer.relative.topPx(8).$} onClick={() => setShow(false)}>
              {!duration && !isMobile && <Icon name="x" />}
            </div>
            <div css={Css.df.w("90%").gap2.if(!!duration).pt3.$}>
              <div css={Css.df.aifs.$}>
                {/* We dont have an official `success-check` icon from design */}
                {variant === "success" && (
                  <div css={Css.df.h3.w3.aic.jcc.bgGreen500.br100.$}>
                    <Icon name="checkmark-white" />
                  </div>
                )}
                {variant === "warning" && <Icon name="warning-icon" />}
                {variant === "error" && <Icon name="error-icon" />}
              </div>
              <div css={Css.df.fdc.$}>
                <div css={Css.t16.fw700.$}>{title}</div>
                {body && <div css={Css.t14.pt1.$}>{body}</div>}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
